import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import QuoteCard from "@/components/QuoteCard";
import Marquee from "@/components/Marquee";
import NewsletterSignup from "@/components/NewsletterSignup";
import { ArrowRight, ExternalLink } from "lucide-react";

const CATEGORY_ORDER = ["Politics", "Business", "Tech", "Travel", "Automotive", "Fashion", "Food", "Sports", "Books", "Opinion"];

export default function Home() {
  const [quotes, setQuotes] = useState([]);
  const [merch, setMerch] = useState([]);
  const [stats, setStats] = useState({ total_quotes: 0, avg_honesty: 0 });

  useEffect(() => {
    api.get("/quotes", { params: { limit: 500 } }).then(r => setQuotes(r.data));
    api.get("/merch").then(r => setMerch(r.data));
    api.get("/stats").then(r => setStats(r.data));
  }, []);

  const { hero, secondary, marqueeItems, byCategory, trending } = useMemo(() => {
    const featured = quotes.filter(q => q.featured);
    const sortedByHonest = [...quotes].sort((a, b) => (b.honesty_score || 0) - (a.honesty_score || 0));
    const filled = featured.length >= 3 ? featured.slice(0, 3) :
      [...featured, ...sortedByHonest.filter(q => !featured.some(f => f.id === q.id)).slice(0, 3 - featured.length)];
    const heroQuote = filled[0] || quotes[0];
    const sec = filled.slice(1);

    const groups = {};
    quotes.forEach(q => { (groups[q.category] ||= []).push(q); });
    const byCat = CATEGORY_ORDER
      .map(c => ({ cat: c, items: (groups[c] || []).slice(0, 4) }))
      .filter(g => g.items.length > 0);

    const trend = [...quotes]
      .sort((a, b) => (b.like_count + b.save_count * 1.5 - b.dislike_count * 0.5) -
                      (a.like_count + a.save_count * 1.5 - a.dislike_count * 0.5))
      .slice(0, 4);

    return {
      hero: heroQuote,
      secondary: sec,
      marqueeItems: [...new Set(quotes.map(q => q.speaker))].slice(0, 12),
      byCategory: byCat,
      trending: trend,
    };
  }, [quotes]);

  return (
    <div className="fade-up">
      {/* Editorial masthead strip */}
      <div className="border-b-ink bg-[#F4F4F0]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-10 py-3 flex items-center justify-between font-mono text-[10px] md:text-[11px] gap-2">
          <span data-testid="masthead-date" className="truncate">Vol. I — {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</span>
          <span className="hidden md:inline">Global candour index: <b>{stats.avg_honesty}</b> / 100 across <b>{stats.total_quotes}</b> filed statements</span>
          <span className="text-[#FF3333]">LIVE ARCHIVE</span>
        </div>
      </div>

      {/* Hero Bento */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-10 py-8 md:py-16" data-testid="hero-section">
        <div className="grid grid-cols-12 gap-4 md:gap-6">
          <div className="col-span-12 md:col-span-8">
            <div className="mb-3 flex items-center gap-3 flex-wrap">
              <span className="tag-red">The Record</span>
              <span className="font-mono text-xs opacity-70">Est. 2026 — every honest sentence, catalogued.</span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-[92px] leading-[0.95] tracking-tight" data-testid="hero-heading">
              To Be<br /><em className="font-quote text-[#FF3333]">Very</em> Honest.
            </h1>
            <p className="mt-6 font-body text-base md:text-lg max-w-2xl">
              The four words that precede the world's most useful sentences — from politicians who slip, CEOs who confess, and travellers who compare. We catalogue them. We rate their candour. Then we point you to what to do about it.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/feed" className="btn-ink inline-flex items-center gap-2" data-testid="hero-cta-feed">
                Read the archive <ArrowRight size={16} />
              </Link>
              <Link to="/shop" className="btn-outline inline-flex items-center gap-2" data-testid="hero-cta-shop">
                Shop the merch <ExternalLink size={14} />
              </Link>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-3 text-[10px] font-ui" data-testid="badge-legend">
              <span className="inline-flex items-center gap-1 border border-[#0000FF] text-[#0000FF] px-2 py-1">✓ VERIFIED</span>
              <span className="opacity-70 normal-case tracking-normal font-body">= real quote + real source.</span>
              <span className="inline-flex items-center gap-1 border border-[#FF3333] text-[#FF3333] bg-[#FFE9E9] px-2 py-1">🤖 AI-ILLUSTRATED</span>
              <span className="opacity-70 normal-case tracking-normal font-body">= we made it up, clearly.</span>
              <span className="inline-flex items-center gap-1 border border-[#0A0A0A]/40 text-[#0A0A0A]/70 px-2 py-1">! PARAPHRASE</span>
              <span className="opacity-70 normal-case tracking-normal font-body">= vibes preserved, words fudged.</span>
            </div>
          </div>

          <aside className="col-span-12 md:col-span-4 grid grid-cols-2 gap-4">
            <div className="col-span-2 border-ink bg-white p-5 shadow-brut-sm">
              <div className="font-ui text-[10px] text-[#FF3333]">Today's benchmark</div>
              <div className="font-display text-6xl leading-none mt-2" data-testid="stat-avg-honesty">{stats.avg_honesty || "—"}</div>
              <div className="font-mono text-[11px] opacity-70 mt-1">Avg. AI Honesty across the archive</div>
            </div>
            <div className="border-ink bg-white p-4 shadow-brut-sm">
              <div className="font-ui text-[10px]">Filed</div>
              <div className="font-display text-3xl" data-testid="stat-total">{stats.total_quotes}</div>
              <div className="font-mono text-[10px] opacity-70">statements</div>
            </div>
            <div className="border-ink bg-[#FF3333] text-white p-4 shadow-brut-sm">
              <div className="font-ui text-[10px]">Categories</div>
              <div className="font-display text-3xl">{byCategory.length}</div>
              <div className="font-mono text-[10px] opacity-80">live sections</div>
            </div>
          </aside>
        </div>
      </section>

      <Marquee items={marqueeItems} />

      {/* Featured */}
      {hero && (
        <section className="max-w-[1400px] mx-auto px-4 md:px-10 py-12 md:py-16" data-testid="featured-section">
          <div className="flex items-baseline justify-between mb-6 md:mb-8 gap-3">
            <h2 className="font-display text-3xl md:text-5xl">Featured Confessions</h2>
            <Link to="/feed" className="font-ui text-xs link-blue whitespace-nowrap" data-testid="see-all-feed">See all →</Link>
          </div>
          <div className="grid grid-cols-12 gap-4 md:gap-6">
            <div className="col-span-12 md:col-span-8"><QuoteCard q={hero} size="lg" /></div>
            <div className="col-span-12 md:col-span-4 grid gap-4 md:gap-6">
              {secondary.map((q) => <QuoteCard key={q.id} q={q} />)}
            </div>
          </div>
        </section>
      )}

      {/* Trending strip */}
      {trending.length > 0 && (
        <section className="border-t-ink border-b-ink bg-white py-12 md:py-16" data-testid="trending-section">
          <div className="max-w-[1400px] mx-auto px-4 md:px-10">
            <div className="flex items-baseline justify-between mb-6 md:mb-8 gap-3">
              <div>
                <div className="tag-red inline-block mb-2">Trending</div>
                <h2 className="font-display text-3xl md:text-5xl">What readers reacted to.</h2>
              </div>
              <Link to="/feed" className="font-ui text-xs link-blue whitespace-nowrap">Sort by trending →</Link>
            </div>
            <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6">
              {trending.map((q) => <QuoteCard key={q.id} q={q} />)}
            </div>
          </div>
        </section>
      )}

      {/* Shop teaser */}
      <section className="py-12 md:py-16" data-testid="shop-teaser">
        <div className="max-w-[1400px] mx-auto px-4 md:px-10">
          <div className="flex items-baseline justify-between mb-6 md:mb-8 gap-3">
            <h2 className="font-display text-3xl md:text-5xl">Wear the Truth</h2>
            <Link to="/shop" className="font-ui text-xs link-blue whitespace-nowrap" data-testid="see-all-shop">Shop everything →</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {merch.slice(0, 4).map((m) => (
              <Link key={m.id} to="/shop" className="block border-ink bg-[#F4F4F0] shadow-brut-sm hover-invert" data-testid={`shop-teaser-${m.id}`}>
                <div className="img-frame aspect-[4/5]"><img src={m.image_url} alt={m.title} loading="lazy" /></div>
                <div className="p-4">
                  <div className="font-ui text-[11px] text-[#FF3333]">TBVH Merch</div>
                  <div className="font-display text-lg mt-1 leading-tight">{m.title}</div>
                  <div className="font-mono text-sm mt-2">${m.price_usd.toFixed(2)}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Categories — magazine sections */}
      {byCategory.map((section, idx) => (
        <section
          key={section.cat}
          className={`py-12 md:py-16 ${idx % 2 === 0 ? "bg-white border-y border-[#0A0A0A]" : ""}`}
          data-testid={`section-${section.cat.toLowerCase()}`}
        >
          <div className="max-w-[1400px] mx-auto px-4 md:px-10">
            <div className="flex items-baseline justify-between mb-6 md:mb-8 gap-3 border-b-ink pb-4">
              <div>
                <div className="font-ui text-[10px] text-[#FF3333] mb-1">{String(idx + 1).padStart(2, "0")} · Section</div>
                <h2 className="font-display text-3xl md:text-5xl">{section.cat}</h2>
              </div>
              <Link
                to={`/feed?cat=${encodeURIComponent(section.cat)}`}
                className="font-ui text-xs link-blue whitespace-nowrap"
                data-testid={`see-more-${section.cat.toLowerCase()}`}
              >More in {section.cat} →</Link>
            </div>
            <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6">
              {section.items.map((q) => <QuoteCard key={q.id} q={q} />)}
            </div>
          </div>
        </section>
      ))}

      {/* Newsletter */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-10 py-10 md:py-16">
        <NewsletterSignup variant="inline" />
      </section>
    </div>
  );
}
